import { useNavigate } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { PageSEO } from "@/components/SEO";
import { useApiGet } from "@/hooks/useApi";

const BlogCard = ({ post }) => {
  const navigate = useNavigate();
  return (
    <article data-testid={`blog-post-${post.id}`} onClick={() => navigate(`/blog/${post.id}`)}
      className="group cursor-pointer flex flex-col md:flex-row gap-8 p-8 rounded-2xl transition-all duration-500 hover:-translate-y-1"
      style={{ background: "white", border: "1px solid var(--ll-border)" }}>
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs tracking-[0.15em] uppercase px-3 py-1 rounded-full" style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{post.category}</span>
          <span className="flex items-center gap-1 text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}><Clock size={12} />{post.read_time}</span>
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl font-medium mb-3 transition-colors group-hover:opacity-80" style={{ color: "var(--ll-text)" }}>{post.title}</h2>
        <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>{post.excerpt}</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{post.author}</p>
            <p className="text-xs mt-1" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
              {new Date(post.date).toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          </div>
          <span className="text-sm font-medium flex items-center gap-1 transition-all duration-300 group-hover:gap-2" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
            Read More <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </article>
  );
};

const BlogPage = () => {
  const { data, loading } = useApiGet("/blog");
  const posts = data?.posts || [];

  return (
    <main data-testid="blog-page" className="pt-20">
      <PageSEO
        title="Blog — Wellness Insights & Burnout Resources"
        description="Expert articles on burnout prevention, work-life balance, self-care, and mental wellness. Practical advice for professionals in South Africa and beyond."
        path="/blog"
        type="blog"
      />
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mb-16">
            <p className="text-xs tracking-[0.3em] uppercase mb-6" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Our Blog</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.1] mb-6" style={{ color: "var(--ll-text)" }}>
              Insights & <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>Resources</em>
            </h1>
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              Practical advice, expert insights, and inspiring stories to support your mental wellbeing journey.
            </p>
          </div>

          {loading && (
            <p className="text-sm py-12 text-center" style={{ color: "var(--ll-text-muted)" }}>Loading articles...</p>
          )}

          <div className="space-y-8" data-testid="blog-posts-container">
            {posts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogPage;
